import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../services/api';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles: string[];
}

const NAV_ITEMS: NavItem[] = [
  // Admin Navigation
  { path: '/admin/dashboard', label: 'Dashboard', icon: '📊', roles: ['admin'] },
  { path: '/admin/users', label: 'User Management', icon: '👥', roles: ['admin'] },
  { path: '/admin/resellers', label: 'Resellers', icon: '🤝', roles: ['admin'] },
  { path: '/admin/clients', label: 'Clients', icon: '👤', roles: ['admin'] },
  { path: '/admin/plans', label: 'Subscription Plans', icon: '💳', roles: ['admin'] },
  { path: '/admin/features', label: 'Features', icon: '⚙️', roles: ['admin'] },
  { path: '/admin/commissions', label: 'Commissions', icon: '💰', roles: ['admin'] },
  { path: '/admin/activity', label: 'Activity Logs', icon: '📋', roles: ['admin'] },
  
  // Reseller Navigation
  { path: '/reseller/dashboard', label: 'Dashboard', icon: '📊', roles: ['reseller'] },
  { path: '/reseller/clients', label: 'My Clients', icon: '👤', roles: ['reseller'] },
  { path: '/reseller/create-client', label: 'Create Client', icon: '➕', roles: ['reseller'] },
  { path: '/reseller/commissions', label: 'My Commissions', icon: '💰', roles: ['reseller'] },
  
  // Client Navigation
  { path: '/client/dashboard', label: 'Dashboard', icon: '📊', roles: ['client'] },
  { path: '/client/engine', label: 'GEO-PRO Engine', icon: '🚀', roles: ['client'] },
  { path: '/client/subscription', label: 'My Subscription', icon: '💳', roles: ['client'] },
  { path: '/client/usage', label: 'Usage History', icon: '📈', roles: ['client'] },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredNavItems = NAV_ITEMS.filter(item => 
    item.roles.some(role => hasRole(role))
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLogoutAllSessions = async () => {
    if (window.confirm('This will log you out from all devices and sessions. Continue?')) {
      await api.logoutAllSessions();
      await logout();
      navigate('/login');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'reseller': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'client': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default: return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
    }
  };

  return (
    <div className="flex h-screen bg-[#0d0d0d] overflow-hidden">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 bg-[#121212] border-r border-[#262626]
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarOpen ? 'lg:w-72' : 'lg:w-20'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="h-16 border-b border-[#262626] flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-900/20">
              <span className="text-xl font-bold text-white">G</span>
            </div>
            {sidebarOpen && (
              <div className="hidden lg:block">
                <h1 className="text-lg font-bold tracking-tight text-white">GEO-PRO</h1>
                <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest">SaaS Platform</p>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-[#1a1a1a] text-neutral-400 transition-colors"
          >
            {sidebarOpen ? '←' : '→'}
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#1a1a1a] text-neutral-400"
          >
            ✕
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-[#262626]">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'lg:justify-center'}`}>
            <div className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center border border-[#262626]">
              <span className="text-lg">👤</span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.firstName || user?.email?.split('@')[0]}
                </p>
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${getRoleBadgeColor(user?.role || '')}`}>
                  {user?.role}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {sidebarOpen && (
            <p className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">
              Navigation
            </p>
          )}
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 p-3 rounded-xl transition-all duration-200 group
                  ${isActive 
                    ? 'bg-[#1a1a1a] border border-[#262626] ring-1 ring-emerald-500/20' 
                    : 'hover:bg-[#1a1a1a] border border-transparent'}
                  ${!sidebarOpen && 'lg:justify-center'}
                `}
              >
                <span className="text-xl">{item.icon}</span>
                {sidebarOpen && (
                  <span className={`text-sm font-medium transition-colors ${
                    isActive ? 'text-emerald-400' : 'text-neutral-400 group-hover:text-neutral-200'
                  }`}>
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Buttons */}
        <div className="p-4 border-t border-[#262626] space-y-2">
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 p-3 rounded-xl
              bg-red-500/10 hover:bg-red-500/20 border border-red-500/20
              text-red-400 transition-all
              ${!sidebarOpen && 'lg:justify-center'}
            `}
          >
            <span className="text-xl">🚪</span>
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
          {sidebarOpen && (
            <button
              onClick={handleLogoutAllSessions}
              className="w-full flex items-center gap-3 p-2 rounded-xl bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-neutral-500 hover:text-neutral-400 transition-all text-xs"
            >
              <span>🔒</span>
              <span>Logout All Sessions</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-[#262626] flex items-center justify-between px-4 lg:px-8 bg-[#0d0d0d]/80 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-[#1a1a1a] text-neutral-400"
            >
              ☰
            </button>
            <div>
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                {filteredNavItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
              <p className="text-[11px] text-neutral-500">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:flex px-3 py-1 bg-[#1a1a1a] border border-[#262626] rounded-full">
              <span className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest">
                Dhaka, Bangladesh
              </span>
            </div>
            <button className="relative p-2 rounded-lg hover:bg-[#1a1a1a] text-neutral-400 transition-colors">
              🔔
              <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
